import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Download, FileJson, FileSpreadsheet, ShieldCheck,
} from 'lucide-react';
import { adminGet, errMsg } from './api';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ExportTab({ setup, loadingSetup, onAfter }) {
  const [collection, setCollection] = useState('bookings');
  const [format, setFormat] = useState('csv');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const activeCollection = setup.collections.find(c => c.collection === collection);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format });
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await adminGet(
        `${API}/api/admin/db/export/${collection}?${params}`,
        { responseType: 'blob' }
      );
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `smashzone-${collection}.${format}`;
      const blob = new Blob([res.data], {
        type: format === 'json' ? 'application/json' : 'text/csv',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${collection} as ${format.toUpperCase()}`);
      onAfter?.();
    } catch (e) {
      toast.error(await errMsg(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="p-6" data-testid="export-card">
      <h2 className="text-lg font-bold uppercase mb-4 flex items-center gap-2">
        <Download className="w-5 h-5 text-emerald-600" /> Export Collection
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-xs uppercase font-bold text-muted-foreground mb-1 block">Collection</Label>
          <Select value={collection} onValueChange={setCollection} disabled={loadingSetup}>
            <SelectTrigger data-testid="collection-select">
              <SelectValue placeholder="Pick a collection" />
            </SelectTrigger>
            <SelectContent>
              {setup.collections.map(c => (
                <SelectItem key={c.collection} value={c.collection}>
                  {c.collection} ({c.approx_rows} rows · {c.field_count} fields)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase font-bold text-muted-foreground mb-1 block">Format</Label>
          <Select value={format} onValueChange={setFormat}>
            <SelectTrigger data-testid="format-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(setup.formats?.length ? setup.formats : ['csv', 'json']).map(f => (
                <SelectItem key={f} value={f}>{f.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-xs uppercase font-bold text-muted-foreground mb-1 block">Date From (optional)</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from" />
        </div>
        <div>
          <Label className="text-xs uppercase font-bold text-muted-foreground mb-1 block">Date To (optional)</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to" />
        </div>
      </div>

      {activeCollection && (
        <div className="mb-4 p-3 rounded-xl bg-slate-50 border border-slate-200" data-testid="field-summary">
          <p className="text-xs uppercase font-bold text-slate-700 mb-1">
            Fields included ({activeCollection.field_count})
          </p>
          <p className="text-xs text-slate-600 break-words">
            {activeCollection.exportable_fields.join(', ')}
          </p>
          <p className="text-xs text-emerald-700 mt-2 font-semibold">
            <ShieldCheck className="w-3 h-3 inline mr-1" />
            Sensitive fields (password hashes, payment tokens, OTPs) are stripped server-side.
          </p>
        </div>
      )}

      <Button
        onClick={handleExport}
        disabled={exporting || loadingSetup}
        className="btn-primary"
        data-testid="export-btn"
      >
        {format === 'json'
          ? <FileJson className="w-4 h-4 mr-2" />
          : <FileSpreadsheet className="w-4 h-4 mr-2" />}
        {exporting ? 'Exporting...' : `Export ${format.toUpperCase()}`}
      </Button>
    </Card>
  );
}
